import { Controller, FormProvider, useForm } from 'react-hook-form';
import greyDialogTitleSx from '../../components/Dialog/greyDialogTitleSx';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Avatar, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, FormHelperText, Grid, IconButton, ListItem, ListItemAvatar,
  ListItemText, Radio, RadioGroup, TextField, Typography
} from '@mui/material';
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { addUser, fetchUserByEmail, fetchUserById, updateUser } from '../../store/userThunk';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { IUserFormInputs } from "../../utils/user.types";
import { ApiStatus } from "../../utils/api.types";
import { fetchRoles } from "../../store/rolesThunk";
import { IOptions } from "../../components/FormFields/CustomSelect";
import useAccountDialCode, { stripDialCode, withDialCode } from "../../utils/useAccountDialCode";
import '../../components/Invoice/Invoice.css'; // provides the "c-label" / "c-text-field-4" field styles

interface IUserFormFields {
  name: string;
  email: string;
  phone: string;
  role: string;
  password: string;
};

export interface IUserFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present = edit an existing member; absent = invite a new one. */
  user?: any;
}

// Same field styling as the account form (DialogForm).
const inputSx = { width: '100%', mt: 0.5, borderRadius: '8px!important' };
const errorText = (message?: string) => message
  ? <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{message}</p>
  : null;

const schema = yup.object().shape({
  name: yup.string().trim().required('Name is required.'),
  email: yup.string().trim().email('Enter a valid email address.').required('Email is required.'),
  phone: yup.string().trim(),
  role: yup.string().required('Role is required.'),
  password: yup.string().test('len', 'Password must be at least 8 characters.', v => !v || v.length >= 8),
});

export default function UserFormDialog({ open, onClose, user }: IUserFormDialogProps) {
  const dispatch = useAppDispatch();
  const { selectedAccount, user: authUser } = useAppSelector((state => state.auth));
  const { status } = useAppSelector((state) => state.users);

  const isEdit = !!user?._id;
  const isSelf = user?._id === authUser?._id;

  const [sendInvite, setSendInvite] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [userRoles, setUserRoles] = useState<IOptions[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Phone prefix follows the account's Country Of Reg.
  const dialCode = useAccountDialCode();

  useEffect(() => {
    const getRoles = async () => {
      const data: any = await dispatch(fetchRoles());
      const roles = Array.isArray(data?.payload) ? data.payload : [];
      setUserRoles(roles.map((c: any) => {
        return { label: c.role.charAt(0).toUpperCase() + c.role.slice(1), value: c._id }
      }));
    }
    getRoles();
  }, [dispatch]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const methods = useForm<IUserFormFields>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { name: '', email: '', phone: '', role: '', password: '' },
  });
  const { control } = methods;
  const hasPhone = !!String(methods.watch('phone') || '').trim();

  // Refill every time the dialog opens so edits that were cancelled don't linger
  useEffect(() => {
    if (!open) return;
    setSelectedMember(null);
    setOptions([]);
    setSendInvite(false);
    setShowPassword(false);
    setIsActive(isEdit ? !!user.isActive : true);
    setMfaEnabled(!!user?.mfa?.enabled);
    // Enabled with nothing ticked means the code goes to email, so show that.
    setMfaMethods(user?.mfa?.methods?.length ? user.mfa.methods : (user?.mfa?.enabled ? ['email'] : []));
    methods.reset({
      name: user?.name || '',
      email: user?.email || '',
      phone: stripDialCode(user?.phone, dialCode),
      // Single-user API returns `role`, list API returns `roles`
      role: user?.role?._id || user?.roles?.[0]?._id || '',
      password: '',
    });
  }, [open, user, isEdit, dialCode, methods]);

  const handleClose = () => {
    methods.reset({ name: '', email: '', phone: '', role: '', password: '' });
    onClose();
  };

  const onSubmit = async (data: IUserFormFields) => {
    try {
      const payload: IUserFormInputs = {
        name: data.name.trim(),
        email: data.email.trim(),
        phone: withDialCode(data.phone, dialCode),
        // Backend reads roles[0] and needs the account to find the role mapping
        roles: data.role ? [data.role] : [],
        accounts: [selectedAccount._id],
        isActive: isEdit ? isActive : false,
      };
      const mfaPicked = mfaEnabled ? mfaMethods.filter(m => m !== 'phone' || hasPhone) : [];
      payload.mfa = { enabled: mfaPicked.length > 0, methods: mfaPicked };
      if (isEdit) {
        await dispatch(updateUser({ id: user._id, userUpdatePayload: payload })).unwrap();
        dispatch(fetchUserById(user._id));
      } else {
        // The member sets their own password from the invite link.
        await dispatch(addUser({ ...payload, password: sendInvite ? '' : data.password, sendInvite })).unwrap();
      }
      handleClose();
    } catch (error: any) {
      // Empty catch block.
    }
  };

  // Debounced so we don't hit the API on every keystroke
  const searchMembers = (value: string) => {
    clearTimeout(searchTimer.current);
    if (!value || value.length < 2) {
      setOptions([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const result: any = await dispatch(fetchUserByEmail({ email: value }));
      setOptions(Array.isArray(result?.payload) ? result.payload : []);
      setSearching(false);
    }, 300);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <FormProvider {...methods}>
        <DialogTitle sx={greyDialogTitleSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5">{isEdit ? 'Edit Member' : 'Member Invite'}</Typography>
              <Typography variant="body2">
                {isEdit
                  ? 'Update the member’s details, role and status'
                  : `Invite a member to ${selectedAccount?.displayName || selectedAccount?.name || 'your business'}`}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container rowSpacing={3} columnSpacing={3} sx={{ pt: 3 }}>
            <Grid item xs={12}>
              <h5 className='c-label'>Email *</h5>
              <Controller
                name='email'
                control={control}
                render={({ field, fieldState }) => (
                  isEdit ? (
                    <>
                      <TextField
                        {...field}
                        placeholder="Enter email"
                        variant="standard"
                        size="small"
                        className='c-text-field-4'
                        sx={inputSx}
                        InputProps={{ disableUnderline: true }}
                      />
                      {errorText(fieldState.error?.message)}
                    </>
                  ) : (
                    <Autocomplete
                      freeSolo
                      options={options}
                      value={selectedMember}
                      inputValue={field.value || ''}
                      filterOptions={(x) => x}
                      loading={searching}
                      getOptionLabel={(option: any) => (typeof option === 'string' ? option : option?.email || '')}
                      isOptionEqualToValue={(option: any, value: any) => option?.email === (value?.email ?? value)}
                      onInputChange={(_event, value, reason) => {
                        field.onChange(value);
                        if (reason === 'input') {
                          setSelectedMember(null);
                          searchMembers(value);
                        }
                      }}
                      onChange={(_event, newValue: any) => {
                        if (newValue && typeof newValue === 'object') {
                          setSelectedMember(newValue);
                          field.onChange(newValue.email);
                          methods.setValue('name', newValue.name || '', { shouldValidate: true });
                          methods.setValue('phone', stripDialCode(newValue.phone || newValue.mobile, dialCode));
                        } else {
                          setSelectedMember(null);
                          field.onChange(newValue || '');
                        }
                      }}
                      onBlur={field.onBlur}
                      renderOption={(props, option: any) => (
                        <ListItem {...props} key={option._id || option.email} dense>
                          <ListItemAvatar>
                            <Avatar src={option.profilePic} imgProps={{ referrerPolicy: 'no-referrer' }}>
                              {option.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={option.name || option.email} secondary={option.email} />
                        </ListItem>
                      )}
                      renderInput={(params) => (
                        <>
                          <TextField
                            {...params}
                            placeholder="Type an email, or search an existing member"
                            variant="standard"
                            size="small"
                            className='c-text-field-4'
                            sx={inputSx}
                            autoFocus
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              endAdornment: (
                                <>
                                  {searching ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                          {errorText(fieldState.error?.message)}
                        </>
                      )}
                    />
                  )
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <h5 className='c-label'>Name *</h5>
              <Controller
                name='name'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <TextField
                      {...field}
                      placeholder="Enter name"
                      variant="standard"
                      size="small"
                      className='c-text-field-4'
                      sx={inputSx}
                      InputProps={{ disableUnderline: true }}
                    />
                    {errorText(fieldState.error?.message)}
                  </>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <h5 className='c-label'>Phone</h5>
              <Controller
                name='phone'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter phone"
                    variant="standard"
                    size="small"
                    className='c-text-field-4'
                    sx={inputSx}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: dialCode ? <span style={{ color: '#9da7b7', marginRight: 6, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>{dialCode}</span> : undefined,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='role'
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl error={!!fieldState.error} fullWidth disabled={isSelf}>
                    <h5 className='c-label'>Role *</h5>
                    {/* Inline radios instead of a Select so the options stay inside the dialog */}
                    <RadioGroup {...field}>
                      <Grid container>
                        {userRoles.map(role => (
                          <Grid item xs={6} sm={4} key={role.value}>
                            <FormControlLabel
                              value={role.value}
                              control={<Radio size="small" />}
                              label={role.label}
                              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                    {isSelf && <FormHelperText>You can’t change your own role.</FormHelperText>}
                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              {isEdit ? (
                <FormControlLabel
                  control={<Checkbox checked={isActive} onChange={(_e, checked) => setIsActive(checked)} size="small" />}
                  label={<Typography variant="body2">Active member</Typography>}
                />
              ) : (
                <>
                  <FormControlLabel
                    control={<Checkbox checked={sendInvite} onChange={(_e, checked) => setSendInvite(checked)} size="small" />}
                    label={<Typography variant="body2">Send invite email</Typography>}
                  />
                  <FormHelperText error sx={{ mt: 0 }}>
                    Tick this only if the email address is real — the invite is sent immediately and cannot be recalled.
                  </FormHelperText>
                </>
              )}

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={mfaEnabled} onChange={(_e, checked) => { setMfaEnabled(checked); if (checked && !mfaMethods.length) setMfaMethods(['email']); }} size="small" />}
                  label={<Typography variant="body2">Require two-factor at sign-in</Typography>}
                />
                {mfaEnabled && (
                  <Box sx={{ pl: 4 }}>
                    {['email', 'phone'].map(method => (
                      <FormControlLabel
                        key={method}
                        control={
                          <Checkbox
                            size="small"
                            disabled={method === 'phone' && !hasPhone}
                            checked={mfaMethods.includes(method) && (method !== 'phone' || hasPhone)}
                            onChange={(_e, checked) => {
                              const next = checked ? [...mfaMethods, method] : mfaMethods.filter(m => m !== method);
                              setMfaMethods(next);
                              if (!next.length) setMfaEnabled(false); // no channel = two-factor off
                            }}
                          />
                        }
                        label={<Typography variant="body2">Verify {method}</Typography>}
                      />
                    ))}
                    <FormHelperText>
                      The member gets a code on each channel ticked. Passing it also marks that channel verified.
                    </FormHelperText>
                  </Box>
                )}
              </Box>
            </Grid>

            {!isEdit && !sendInvite && (
              <Grid item xs={12} sm={6}>
                <h5 className='c-label'>Password</h5>
                <Controller
                  name='password'
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <TextField
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Leave blank to auto-generate"
                        variant="standard"
                        size="small"
                        className='c-text-field-4'
                        sx={inputSx}
                        autoComplete="new-password"
                        InputProps={{
                          disableUnderline: true,
                          endAdornment: (
                            <IconButton size="small" edge="end" onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          )
                        }}
                      />
                      {errorText(fieldState.error?.message)}
                    </>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            onClick={methods.handleSubmit(onSubmit)}
            loading={status === ApiStatus.LOADING}
            variant="contained"
          >
            {isEdit ? 'Update' : (sendInvite ? 'Create & Send Invite' : 'Create')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
